// loaderPalettes.js — the curated, NAMED, multi-colour palette THEMES the
// BrandWheelLoader autoselects from on EACH generation cycle.
//
// HISTORY:
//   · #937 (Gordon 2026-08-22): "Generate different color palettes (not
//     monochromatic) that the loader autoselects from randomly every cycle."
//   · Gordon DECISION 2026-08-23: OPEN UP to WILDER non-brand hues for more
//     playful per-load variety (SUPERSEDED the brand-jewel-only lock).
//   · QA-1257 (Gordon design request, 2026-08-25) — TWO parts, both handled here:
//       PART A (BUG): the loader "always begins and ends on the same colour".
//         Root cause: a 5-colour palette on the fixed 6-segment loader wrapped
//         (`palette[5 % 5]` === `palette[0]`), colliding segment 5 with segment 0
//         at the circular midnight SEAM. FIX lives in brandWheel.js's
//         `seamSafeSegmentColors` (used by `randomBrandWheel`); this file's part
//         of the fix is that EVERY theme below carries >= LOADER_SEGMENT_COUNT (6)
//         DISTINCT colours, so the seam-safe selection stays entirely inside the
//         curated palette (never synthesises an off-palette colour) AND every
//         segment gets a unique colour. A build-time invariant test asserts this.
//       PART B (FEATURE): the palettes looked too similar. Replaced the loose
//         colour arrays with distinct NAMED THEMES — a "Standard" brand palette
//         plus visually-separated variety themes (bubble-gum, nature, goth,
//         punk, neutral, loud-neon, under-the-sea, tropical-storm, desert-sunset,
//         forest-canopy, cotton-candy, monochrome).
//       COORDINATOR ADD (2026-08-25): "Standard" (the app's CURRENT default brand
//         segment colours) is the DEFAULT theme (index 0, the deterministic /
//         fallback choice) and stays in the rotation pool.
//   · QA-1273 (Gordon REVISION, 2026-08-25): the earlier COORDINATOR weighting
//     over-emphasised Standard — at weight 10 vs twelve weight-1 themes it showed
//     ~45% of the time (Gordon saw it 3x in a row). Gordon now wants Standard only
//     OCCASIONAL, with the twelve variety themes showing MORE. Standard's rotation
//     WEIGHT is dropped to 1 (equal to a single theme) => Standard ~= 1/13 ~= 7.7%
//     of draws, and the variety themes collectively dominate (~92%). Standard is
//     NOT removed — just de-emphasised so it is occasional, not dominant.
//
// STILL DISCIPLINED (Nick's principles + "tasteful, not mud"): every theme is
// multi-hue (>=3 distinct hues — in fact >=6), ordered so no two near-identical
// hues sit adjacent (warm/cool alternation), so the wilder colours read as
// vibrant and intentional, not clashing.
//
// Applied ONLY to the main FILLED brand loader (index.jsx routes the chosen
// palette through `randomBrandWheel({ palette })` on each swap, which runs it
// through `seamSafeSegmentColors`). The mono/outline loader variants are
// single-ink by design and ignore this. The PHASE-1 canonical wheel (before the
// loop starts) still uses brandWheel.js's default brand palette.
import { BRAND_WHEEL_COLORS, BRAND_WHEEL_PALETTE } from "../BrandWheel/brandWheel";

// The loader is locked to a fixed 6-segment wheel (index.jsx LOADER_SEGMENT_COUNT).
// Every theme MUST carry at least this many DISTINCT colours so each segment —
// including the circular seam — gets a unique colour with no interpolation.
export const LOADER_SEGMENT_COUNT = 6;

// The seven canonical brand jewel hues (re-exported for tests/consumers that need
// to reason about "brand vs wild"). Kept in sync with brandWheel.js.
export const BRAND_JEWEL_HUES = [...BRAND_WHEEL_COLORS];
const BRAND_SET = new Set(BRAND_JEWEL_HUES);

// ── The NAMED palette themes ────────────────────────────────────────────────
// Each `colors` array is ordered (warm/cool alternation) and carries >= 6
// DISTINCT colours. `weight` sets how often the theme is drawn on the random
// per-cycle autoselect. QA-1273 (Gordon 2026-08-25): every theme — including
// "standard" — carries EQUAL weight 1, so Standard is only OCCASIONAL (~1/13 of
// draws) and the twelve variety themes collectively dominate the rotation.
export const LOADER_THEMES = [
  // STANDARD / CLASSIC — the app's CURRENT default brand segment colours
  // (brandWheel.js BRAND_WHEEL_PALETTE). The DEFAULT/fallback theme (index 0),
  // kept in the pool but de-emphasised to OCCASIONAL (QA-1273: weight 10 -> 1).
  { name: "standard", label: "Standard", weight: 1, colors: [...BRAND_WHEEL_PALETTE] },

  // bubble-gum — bright candy pinks with pop accents
  { name: "bubble-gum", label: "Bubble Gum", weight: 1,
    colors: ["#FF4FA3", "#E56FB6", "#C77DFF", "#FF85CB", "#A0F0D0", "#FFD54F"] },

  // nature — leaf greens + earth/bark
  { name: "nature", label: "Nature", weight: 1,
    colors: ["#2E7D32", "#C0CA33", "#6D4C41", "#8BC34A", "#4DB6AC", "#A1887F", "#33691E"] },

  // goth / vampire — deep black, blood red, vampire purple
  { name: "goth-vampire", label: "Goth / Vampire", weight: 1,
    colors: ["#1A1A1D", "#7B0F1B", "#4A0E4E", "#B01030", "#2C2C34", "#6A1B9A"] },

  // punk — deep royal-purple anchor + hot magenta + electric acid. QA-1280
  // (Gordon 2026-08-25): the old #111114 near-black anchor is gone (only Goth may
  // be black); #3D1466 keeps punk's dark, high-contrast edge as a real purple hue.
  { name: "punk", label: "Punk", weight: 1,
    colors: ["#3D1466", "#FF008C", "#00E5FF", "#FDE047", "#7C3AED", "#FF3B30"] },

  // muted-earth (formerly "neutral" greys) — QA-1280 (Gordon 2026-08-25): only Goth
  // may be a black/grey palette, so the pure greys are replaced by soft, HUED earth
  // tones (clay / sand / sage / cream / taupe / dusty-blue / dusty-rose). Still soft
  // & muted — the theme's character — but no longer a neutral-grey palette.
  { name: "neutral", label: "Muted Earth", weight: 1,
    colors: ["#A88B6A", "#C9BBA8", "#79945E", "#D6C7B0", "#B3A188", "#9BB0BF", "#A98594"] },

  // loud-neon — electric saturated neons
  { name: "loud-neon", label: "Loud Neon", weight: 1,
    colors: ["#39FF14", "#FF073A", "#0FF0FC", "#FF00FF", "#FFAA00", "#FE53BB"] },

  // under-the-sea — aqua / teal / coral / sand / deep blue
  { name: "under-the-sea", label: "Under the Sea", weight: 1,
    colors: ["#17A2B8", "#0D3B66", "#FF7F6B", "#E8D6A0", "#2EC4B6", "#56CFE1", "#4A6FA5"] },

  // tropical-storm — deep stormy BLUES + a lightning-yellow accent. QA-1280
  // (Gordon 2026-08-25): the near-black slate #263238 is gone and the grey-blues are
  // pushed to real, saturated storm-blues so the theme reads as a moody sky, not grey.
  { name: "tropical-storm", label: "Tropical Storm", weight: 1,
    colors: ["#274B6B", "#FFC400", "#3E7CA6", "#1F5673", "#7FB2D9", "#345B86"] },

  // desert-sunset — terracotta / gold sands + a dusk cool
  { name: "desert-sunset", label: "Desert Sunset", weight: 1,
    colors: ["#E76F51", "#E9C46A", "#6D4C6D", "#F4A261", "#C05746", "#F6BD60"] },

  // forest-canopy — layered greens + a bark accent
  { name: "forest-canopy", label: "Forest Canopy", weight: 1,
    colors: ["#1B4332", "#A7C957", "#6D4C41", "#52B788", "#40916C", "#95D5B2"] },

  // cotton-candy — soft pastels
  { name: "cotton-candy", label: "Cotton Candy", weight: 1,
    colors: ["#FFB5E8", "#B5DEFF", "#D5B5FF", "#7DE3B0", "#FFE066", "#FFC9DE"] },

  // monochrome — a single-HUE (blue-steel) tonal ramp. QA-1280 (Gordon 2026-08-25):
  // a pure greyscale ramp is a black/grey palette and only Goth may be one, so the
  // ramp keeps its light→dark tonal CHARACTER but every step is a real blue hue
  // (no black, no neutral grey). Distinct lightness steps => still seam-safe.
  { name: "monochrome", label: "Steel Blue", weight: 1,
    colors: ["#B4CFE8", "#8DB2D8", "#6693C4", "#46709E", "#2E4E72", "#1B3350"] },
];

// The DEFAULT theme (index 0 / deterministic fallback). QA-1273: no longer the
// highest-weighted — it now carries the same weight as every variety theme, so it
// is only occasionally shown on the weighted random draw.
export const STANDARD_THEME_NAME = "standard";

// QA-1280 (Gordon 2026-08-25): "Keep exactly ONE black/gray palette = GOTH."
// goth-vampire is the single theme permitted to carry near-black / grey segments
// (broken up by its bloody-red tones); EVERY other theme must be black-free.
export const GOTH_THEME_NAME = "goth-vampire";

// Back-compat: the flat list of colour arrays (index.jsx + BrandWheelLoader.test's
// "every fill is from a curated palette" guard consume `LOADER_PALETTES.flat()`).
// Order matches LOADER_THEMES, so index 0 is the Standard palette.
export const LOADER_PALETTES = LOADER_THEMES.map((t) => t.colors);

export const THEME_NAMES = LOADER_THEMES.map((t) => t.name);

// Look up a theme by name (returns undefined for an unknown name).
export const getLoaderTheme = (name) => LOADER_THEMES.find((t) => t.name === name);

// True if `c` is NOT one of the seven brand jewel hues (i.e. a wild colour).
export const isWildHue = (c) => !BRAND_SET.has(c);

// Count of distinct hues in a palette (a monochromatic palette => 1).
export const paletteDistinctHueCount = (palette) => new Set(palette).size;

// The wild (non-brand) colours present in a single palette.
export const wildHuesInPalette = (palette) => palette.filter(isWildHue);

// ── QA-1257 (Gordon 2026-08-25): "All of the palettes have a white space, we do
// not want any white segments." A segment reads as the WHITE loader background —
// i.e. an invisible gap — when it is either (a) so light it has almost no contrast
// against white (high WCAG relative luminance — this also catches bright yellows,
// which are famously hard to see on white), or (b) so desaturated-and-light that
// it is mostly white ink (a high "white floor": every RGB channel high). Either
// gate ⇒ near-white ⇒ NOT a valid loader segment colour. Mechanical guard so a
// future palette edit can't reintroduce a white/near-white segment unnoticed.
export const NEAR_WHITE_MAX_LUMINANCE = 0.8; // relative luminance above this ⇒ too low-contrast on white
export const NEAR_WHITE_MIN_CHANNEL = 205; // min(R,G,B) at/above this ⇒ washed-out (too much white)

const _hexToRgb = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const _srgbToLinear = (c) => {
  const cs = c / 255;
  return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
};
// WCAG relative luminance of a hex colour (0 = black, 1 = white).
export const relativeLuminance = (hex) => {
  const [r, g, b] = _hexToRgb(hex).map(_srgbToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
// True when a colour would read as (near-)white against the white loader bg.
export const isNearWhite = (hex) => {
  const rgb = _hexToRgb(hex);
  return (
    relativeLuminance(hex) > NEAR_WHITE_MAX_LUMINANCE ||
    Math.min(...rgb) >= NEAR_WHITE_MIN_CHANNEL
  );
};
// The near-white colours in one palette (empty ⇒ palette is white-segment-free).
export const nearWhiteColorsInPalette = (palette) => palette.filter(isNearWhite);
// True iff NO loader palette contains any white/near-white colour.
export const everyLoaderPaletteHasNoNearWhite = () =>
  LOADER_PALETTES.every((pal) => nearWhiteColorsInPalette(pal).length === 0);

// How many of the palettes contain at least one wild (non-brand) hue. Gordon's
// 2026-08-23 decision wants this to be a MAJORITY.
export const loaderPalettesWithWildHues = () =>
  LOADER_PALETTES.filter((pal) => wildHuesInPalette(pal).length > 0).length;

// ── QA-1280 (Gordon 2026-08-25): "too many palettes have black + grays. Keep
// exactly ONE black/gray palette = GOTH ... NO OTHER palette may contain black."
// A colour reads as (near-)black when its brightest channel is still very dark —
// i.e. max(R,G,B) is below BLACK_MAX_CHANNEL. This catches pure black and the
// near-blacks Gordon saw on the live loader (#111114 punk, #1B1B1B monochrome,
// #263238 tropical-storm) while leaving genuinely-hued deep tones (deep navies,
// dark forest greens, the goth blood-reds/purples) as valid segment colours.
// Mechanical guard so a future palette edit can't reintroduce black to a non-Goth
// theme unnoticed. Goth (GOTH_THEME_NAME) is the ONE theme allowed to be dark.
export const BLACK_MAX_CHANNEL = 60; // max(R,G,B) < this ⇒ near-black
export const isNearBlack = (hex) => Math.max(..._hexToRgb(hex)) < BLACK_MAX_CHANNEL;
// The near-black colours in one palette (empty ⇒ palette is black-free).
export const nearBlackColorsInPalette = (palette) => palette.filter(isNearBlack);
// True iff NO theme except Goth contains any near-black colour (the QA-1280 law).
export const everyNonGothPaletteHasNoBlack = () =>
  LOADER_THEMES.every(
    (t) => t.name === GOTH_THEME_NAME || nearBlackColorsInPalette(t.colors).length === 0,
  );

// The cumulative-weight table for the weighted random draw.
const TOTAL_WEIGHT = LOADER_THEMES.reduce((sum, t) => sum + t.weight, 0);

// The theme carrying the largest rotation weight. QA-1273: all themes now share
// weight 1, so there is no single dominant theme — this returns the first theme
// reached at the (tied) max weight (Standard, index 0), used only as a helper.
export const highestWeightedThemeName = () =>
  LOADER_THEMES.reduce((best, t) => (t.weight > best.weight ? t : best)).name;

// The total rotation weight of the NON-standard (variety) themes. QA-1273 asserts
// this dwarfs Standard's own weight, i.e. the variety themes dominate the draw.
export const varietyThemesTotalWeight = () =>
  LOADER_THEMES.filter((t) => t.name !== STANDARD_THEME_NAME)
    .reduce((sum, t) => sum + t.weight, 0);

// Standard's share of the total rotation weight (0..1) — QA-1273: a small minority.
export const standardWeightShare = () => {
  const std = LOADER_THEMES.find((t) => t.name === STANDARD_THEME_NAME);
  return (std ? std.weight : 0) / TOTAL_WEIGHT;
};

// Pick a THEME for a generation cycle.
//   · numeric `cycleIndex`  => DETERMINISTIC by index (wrapping mod theme count),
//     every theme reachable — used where a stable, non-random choice is wanted
//     (a still reduced-motion mark, or a test that must not be flaky).
//   · no argument           => WEIGHTED random draw. QA-1273: all themes carry
//     equal weight, so Standard is only occasional and the variety themes dominate.
export function pickLoaderTheme(cycleIndex) {
  const count = LOADER_THEMES.length;
  if (typeof cycleIndex === "number") {
    const i = ((Math.floor(cycleIndex) % count) + count) % count;
    return LOADER_THEMES[i];
  }
  let r = Math.random() * TOTAL_WEIGHT;
  for (const theme of LOADER_THEMES) {
    r -= theme.weight;
    if (r < 0) return theme;
  }
  return LOADER_THEMES[0]; // fp-safety fallthrough
}

// Pick one palette (colour array) for a generation cycle — the loader's own
// entry point. Same selection semantics as pickLoaderTheme (deterministic by
// index / weighted random), returning just the colours.
export function pickLoaderPalette(cycleIndex) {
  return pickLoaderTheme(cycleIndex).colors;
}

// ── QA-1280 (Gordon 2026-08-25): NO CONSECUTIVE REPEAT ───────────────────────
// "The loader must NEVER show the same palette twice in a row." The stateless
// weighted draw above CAN return the same theme on back-to-back calls (Gordon saw
// the same palette 4× consecutively). pickNextLoaderTheme is the LIVE loader's
// draw: a weighted random pick over the pool EXCLUDING the immediately-previous
// theme, so no palette can repeat back-to-back. Module-level state persists across
// calls within a session (the loader remounts do not reset it); tests reset it via
// _resetLoaderRotation(). The pure pickLoaderTheme/pickLoaderPalette above are left
// stateless (deterministic-by-index still works) — only the loader entry point is
// history-aware.
let _lastLoaderThemeName = null;

// Reset the no-repeat history (test seam; also safe to call to force a fresh draw).
export const _resetLoaderRotation = () => {
  _lastLoaderThemeName = null;
};

// Draw the NEXT theme for a live loader cycle: weighted random, EXCLUDING the
// immediately-previous theme so it never repeats twice in a row.
export function pickNextLoaderTheme() {
  const eligible = LOADER_THEMES.filter((t) => t.name !== _lastLoaderThemeName);
  // (eligible is only empty in the degenerate 1-theme case; fall back to the full set)
  const pool = eligible.length ? eligible : LOADER_THEMES;
  const poolWeight = pool.reduce((sum, t) => sum + t.weight, 0);
  let r = Math.random() * poolWeight;
  let chosen = pool[pool.length - 1]; // fp-safety fallthrough
  for (const theme of pool) {
    r -= theme.weight;
    if (r < 0) {
      chosen = theme;
      break;
    }
  }
  _lastLoaderThemeName = chosen.name;
  return chosen;
}

// The palette (colour array) for the next live loader cycle — no back-to-back
// repeats. This is the entry point index.jsx uses on each generation swap.
export function pickNextLoaderPalette() {
  return pickNextLoaderTheme().colors;
}
